import ky, { type SearchParamsOption } from "ky"
import { API_ERROR_CODE, ApiSuccessFlag } from "./constants"

type CompatibleApiResponse<T> = Omit<API.ApiResponse<T>, "success"> & {
  success: boolean | ApiSuccessFlag
}

export class ApiRequestError extends Error {
  errorCode?: number
  status: number

  constructor(
    errorMessage: string,
    options: { errorCode?: number; status: number },
  ) {
    super(errorMessage)
    this.name = "ApiRequestError"
    this.errorCode = options.errorCode
    this.status = options.status
  }
}

const getApiResponse = (value: unknown) => {
  const matched =
    typeof value === "object" &&
    value !== null &&
    "success" in value &&
    (typeof (value as { success?: unknown }).success === "boolean" ||
      typeof (value as { success?: unknown }).success === "number")

  return matched ? (value as CompatibleApiResponse<unknown>) : undefined
}

const getApiErrorCode = (value: unknown) => {
  const apiResponse = getApiResponse(value)

  return typeof apiResponse?.errorCode === "number"
    ? apiResponse.errorCode
    : undefined
}

const getApiErrorMessage = (value: unknown) => {
  const apiResponse = getApiResponse(value)
  if (!apiResponse) return undefined

  const errorMessage = apiResponse.errorMessage
  return typeof errorMessage === "string" && errorMessage.trim()
    ? errorMessage
    : undefined
}

const isUnauthorizedResponse = (value: unknown) => {
  const apiResponse = getApiResponse(value)
  if (!apiResponse) return false

  return (
    !apiResponse.success &&
    apiResponse.errorCode === API_ERROR_CODE.UNAUTHORIZED
  )
}

const getErrorMessage = (data: unknown, status: number) => {
  const apiErrorMessage = getApiErrorMessage(data)
  if (apiErrorMessage) return apiErrorMessage

  if (typeof data === "object" && data !== null) {
    const errorData = data as {
      error?: unknown
      errorMessage?: unknown
      message?: unknown
    }
    const messageValue =
      errorData.errorMessage || errorData.message || errorData.error

    if (typeof messageValue === "string" && messageValue.trim()) {
      return messageValue
    }
  }

  if (typeof data === "string") {
    const trimmedData = data.trim()
    const lowerData = trimmedData.toLowerCase()

    if (
      trimmedData &&
      !lowerData.startsWith("<!doctype html") &&
      !lowerData.startsWith("<html")
    ) {
      return trimmedData
    }
  }

  return `请求失败：HTTP ${status}`
}

const createApiRequestError = (data: unknown, status: number) => {
  return new ApiRequestError(getErrorMessage(data, status), {
    errorCode: getApiErrorCode(data),
    status,
  })
}

export const isUnauthorizedRequestError = (error: unknown) => {
  return (
    error instanceof ApiRequestError &&
    error.errorCode === API_ERROR_CODE.UNAUTHORIZED
  )
}

const parseResponseData = async (response: Response) => {
  const text = await response.text()

  try {
    return JSON.parse(text) as unknown
  } catch {
    return text || "(empty response)"
  }
}

export type RequestHandlers = {
  alwaysHandleUnauthorized?: boolean
  onError?: (error: ApiRequestError) => void
  onUnauthorized?: (error: ApiRequestError) => void
}

let requestHandlers: RequestHandlers = {}

export const configureRequestHandlers = (handlers: RequestHandlers) => {
  requestHandlers = handlers
}

export const request = ky.create({
  credentials: "include",
  retry: 0,
  timeout: 30_000,
  throwHttpErrors: false,
  hooks: {
    afterResponse: [
      async ({ options, response }) => {
        // Binary endpoints still share JSON authentication/error handling, but
        // successful media must not be cloned and decoded as text.
        if (
          response.ok &&
          options.context.responseType === "blob" &&
          !response.headers.get("content-type")?.toLowerCase().includes("json")
        )
          return

        const data = await parseResponseData(response.clone())
        const apiResponse = getApiResponse(data)

        if (
          isUnauthorizedResponse(data) ||
          (requestHandlers.alwaysHandleUnauthorized && response.status === 401)
        ) {
          const error = createApiRequestError(data, response.status)

          if (
            options.context.notify !== false ||
            requestHandlers.alwaysHandleUnauthorized
          ) {
            requestHandlers.onUnauthorized?.(error)
          }

          throw error
        }

        if (apiResponse && !apiResponse.success) {
          const error = createApiRequestError(data, response.status)

          if (options.context.notify !== false) {
            requestHandlers.onError?.(error)
          }

          throw error
        }

        if (!response.ok) {
          const error = createApiRequestError(data, response.status)

          if (options.context.notify !== false) {
            requestHandlers.onError?.(error)
          }

          throw error
        }
      },
    ],
  },
})

export type RequestOptions = {
  notify?: boolean
  searchParams?: SearchParamsOption
  signal?: AbortSignal
}

export const post = async <T, D = unknown>(
  url: string,
  data?: D,
  options: RequestOptions = {},
) => {
  const { notify, searchParams, signal } = options

  return request
    .post(url.trim(), {
      context: {
        notify,
      },
      headers: {
        accept: "application/json",
      },
      json: data,
      searchParams,
      signal,
    })
    .json<API.ApiResponse<T>>()
}
